import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Printer, Share2, ArrowRight, ArrowLeft } from "lucide-react";
import ShareDialog from "./ShareDialog";
import EmailResults from "./EmailResults";

export const ToolHeader = () => (
  <header className="sticky top-0 z-40 border-b border-border bg-background/80 backdrop-blur-xl no-print">
    <div className="max-w-6xl mx-auto px-5 lg:px-10 h-14 flex items-center justify-between">
      <Link to="/" className="font-display font-black text-lg tracking-tight" data-testid="tools-logo">
        Rule<span className="text-primary">AI</span><span className="text-muted-foreground">.systems</span>
      </Link>
      <nav className="flex items-center gap-4">
        <Link to="/tools" className="text-sm hover:text-primary transition-colors" data-testid="tools-nav-all">All tools</Link>
        <Link to="/library" className="text-sm hover:text-primary transition-colors hidden sm:inline" data-testid="tools-nav-library">Library</Link>
        <Link to="/register?source=tools-nav">
          <Button size="sm" className="rounded-sm bg-accent hover:bg-accent/90 text-white" data-testid="tools-nav-register">Founding access</Button>
        </Link>
      </nav>
    </div>
  </header>
);

export const FoundingCTA = () => (
  <section className="border-t border-border no-print" data-testid="tools-founding-cta">
    <div className="max-w-6xl mx-auto px-5 lg:px-10 py-14 grid md:grid-cols-[1fr_auto] gap-6 items-center">
      <div>
        <div className="overline text-accent">◉ Founding access · limited spots</div>
        <h2 className="font-display font-black text-2xl md:text-4xl tracking-tight mt-2">Stop running your plant on spreadsheets.</h2>
        <p className="text-muted-foreground mt-2 max-w-2xl">RuleAI tracks OEE, downtime, 5S, CAPA and shift handovers live — with an AI copilot that writes the SOPs and spots the losses for you.</p>
      </div>
      <Link to="/register?source=tools-cta">
        <Button size="lg" className="rounded-sm h-12 px-6 bg-accent hover:bg-accent/90 text-white" data-testid="tools-founding-cta-btn">
          Claim founding access <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </Link>
    </div>
  </section>
);

export const PoweredBy = () => (
  <footer className="border-t border-border" data-testid="tools-powered-by">
    <div className="max-w-6xl mx-auto px-5 lg:px-10 py-6 flex flex-wrap items-center justify-between gap-3 text-xs font-mono-plex uppercase tracking-widest text-muted-foreground">
      <span>Powered by <span className="text-foreground">Rule<span className="text-primary">AI</span> Systems</span></span>
      <span>Free · No login · ruleai.systems/tools</span>
    </div>
  </footer>
);

export default function ToolShell({ toolKey, overline, title, intro, children, share, results, savePrompt }) {
  const [shareOpen, setShareOpen] = useState(false);
  useEffect(() => { document.title = `${title} — Free tool | RuleAI Systems`; window.scrollTo(0, 0); }, [title]);

  return (
    <div className="dark min-h-screen bg-background text-foreground" data-testid={`tool-page-${toolKey}`}>
      <ToolHeader />
      <div className="print-only px-5 py-3 border-b border-border text-sm font-mono-plex">RuleAI Systems · {title} · {new Date().toLocaleDateString()}</div>
      <section className="border-b border-border grid-lines">
        <div className="max-w-6xl mx-auto px-5 lg:px-10 py-10 md:py-14">
          <Link to="/tools" className="inline-flex items-center text-xs font-mono-plex uppercase tracking-widest text-muted-foreground hover:text-primary no-print" data-testid="tool-back-link">
            <ArrowLeft className="w-3 h-3 mr-1" /> All free tools
          </Link>
          <div className="overline text-accent mt-5">{overline}</div>
          <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-2" data-testid="tool-title">{title}</h1>
          <p className="text-muted-foreground text-sm md:text-base mt-4 max-w-2xl">{intro}</p>
        </div>
      </section>
      <main className="max-w-6xl mx-auto px-5 lg:px-10 py-8 md:py-10 space-y-6">
        {children}
        <div className="flex flex-wrap gap-3 no-print" data-testid="tool-actions">
          <Button onClick={() => window.print()} variant="outline" className="rounded-sm h-11" data-testid="tool-print-btn">
            <Printer className="w-4 h-4 mr-2" /> Print / Save as PDF
          </Button>
          {share && (
            <Button onClick={() => setShareOpen(true)} className="rounded-sm h-11 bg-primary hover:bg-primary/90" data-testid="tool-share-btn">
              <Share2 className="w-4 h-4 mr-2" /> {share.buttonLabel || "Share my result"}
            </Button>
          )}
        </div>
        {savePrompt}
        <EmailResults toolKey={toolKey} results={results} />
      </main>
      <FoundingCTA />
      <PoweredBy />
      {share && <ShareDialog open={shareOpen} onOpenChange={setShareOpen} card={share} />}
    </div>
  );
}
